import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AuthStore } from '@core/auth/auth-store';
import { ApiClient } from '@core/services/api-client';
import { JwtDecoderService } from '@core/services/jwt-decoder-service';
import { Button } from '@shared/components/button/button';

@Component({
  selector: 'app-login',
  imports: [Button, CommonModule, ReactiveFormsModule],
  templateUrl: './login-page.html',
  styles: [
    `
      .floating-shape-1 {
        animation-duration: 6s;
      }
      .floating-shape-2 {
        animation-duration: 25s;
      }
      .floating-shape-3 {
        animation-duration: 20s;
      }
      .floating-shape-4 {
        animation-duration: 18s;
      }
      @keyframes gradientSlide {
        0% {
          background-position: 0% 0%;
        }
        50% {
          background-position: 100% 100%;
        }
        100% {
          background-position: 0% 0%;
        }
      }
    `,
  ],
})
export class LoginPage implements OnInit {
  private apiClient = inject(ApiClient);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private authStore = inject(AuthStore);
  private jwtDecoder = inject(JwtDecoderService);

  loginForm: FormGroup;
  loginError = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    const accessToken = this.getAccessToken('accessToken');
    if (accessToken) {
      this.router.navigate(['/candidates']);
    }
  }

  // Getter methods for form controls
  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }

  // Check if field should show error
  shouldShowError(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  // Get email error message
  getEmailError(): string {
    if (this.email?.hasError('required')) return 'Please enter email address';
    if (this.email?.hasError('email'))
      return 'Please enter valid email address';
    return '';
  }

  // Get password error message
  getPasswordError(): string {
    if (this.password?.hasError('required')) return 'Please enter password';
    return '';
  }

  async onSubmit(event: Event) {
    event.preventDefault();

    // Mark all fields as touched to show validation
    this.loginForm.markAllAsTouched();

    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading.set(true);
    this.loginError.set(false);

    try {
      const { email, password } = this.loginForm.value;
      const result = await this.apiClient.login(email.trim(), password);

      if (result && result.data.accessToken) {
        this.authStore.setAccessToken(result.data.accessToken);
        if (
          this.jwtDecoder.getUserRole(this.authStore.getAccessToken()) ==
          'admin'
        ) {
          this.router.navigate(['/users']);
        } else {
          this.router.navigate(['/candidates']);
        }
      } else {
        this.loginError.set(true);
      }
    } catch (error) {
      console.error('Login error:', error);
      this.loginError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  async loginWithGoogle() {
    try {
      await this.apiClient.getGoogleLoginUrl();
    } catch (error) {
      console.error('Google login error:', error);
      this.loginError.set(true);
    }
  }

  private getAccessToken(name: string): string | null {
    return localStorage.getItem(name) || null;
  }
}
